import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "./supabaseClient";

const SUPABASE_ANON_KEY = "sb_publishable_gsHORs-OWooYG4WzhAIcHw_5D81FM84";
const DAILY_LIMIT = 5;

function ChatCoach({ user }) {
  const [messages, setMessages] = useState([{
    role: "assistant",
    content: "Hey! I am your AI boxing coach. I have access to your profile and recent training sessions so I can give you personalized advice. What do you want to work on today?"
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [questionsToday, setQuestionsToday] = useState(0);
  const [profile, setProfile] = useState(null);
  const [recentFeedback, setRecentFeedback] = useState(null);
  const bottomRef = useRef(null);

  const loadContext = useCallback(async () => {
    const { data: profileData } = await supabase.from("profiles").select("*").eq("id", user.id).single();
    setProfile(profileData);
    const { data: feedbackData } = await supabase.from("feedback_history").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(1).single();
    setRecentFeedback(feedbackData);
  }, [user.id]);

  const loadTodayCount = useCallback(async () => {
    const today = new Date().toISOString().split("T")[0];
    const { data } = await supabase.from("chat_messages").select("id").eq("user_id", user.id).eq("role", "user").gte("created_at", today);
    setQuestionsToday(data?.length || 0);
  }, [user.id]);

  useEffect(() => {
    loadContext();
    loadTodayCount();
  }, [loadContext, loadTodayCount]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const buildSystemPrompt = () => {
    let prompt = "You are an expert boxing coach assistant inside the FightCoach AI app. You give concise, specific, actionable boxing advice. Keep responses under 150 words. Be direct and encouraging like a real coach.";
    if (profile) {
      prompt += " The fighter you are coaching: Name: " + (profile.full_name || "Fighter") + ", Style: " + (profile.boxing_style || "orthodox") + ", Experience: " + (profile.experience || "unknown") + ", Weight class: " + (profile.weight_class || "unknown") + ".";
    }
    if (recentFeedback) {
      prompt += " Their most recent AI coaching session (" + recentFeedback.style + " style) noted: " + recentFeedback.feedback.substring(0, 300) + "...";
    }
    prompt += " Reference their specific details when relevant. Never give generic advice when you have their context.";
    return prompt;
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    if (questionsToday >= DAILY_LIMIT) return;

    const userMessage = { role: "user", content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    await supabase.from("chat_messages").insert({ user_id: user.id, role: "user", content: input.trim() });

    try {
      const conversationMessages = newMessages.map(function(m) { return { role: m.role, content: m.content }; });
      const response = await fetch("https://ybcwbttitirdwbjtitsk.supabase.co/functions/v1/ai-feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + SUPABASE_ANON_KEY },
        body: JSON.stringify({ type: "chat", messages: conversationMessages, systemPrompt: buildSystemPrompt() })
      });

      const data = await response.json();
      if (data.feedback) {
        const assistantMessage = { role: "assistant", content: data.feedback };
        setMessages(function(prev) { return [...prev, assistantMessage]; });
        await supabase.from("chat_messages").insert({ user_id: user.id, role: "assistant", content: data.feedback });
        setQuestionsToday(function(prev) { return prev + 1; });
      }
    } catch (err) {
      setMessages(function(prev) { return [...prev, { role: "assistant", content: "Sorry, something went wrong. Try again in a moment." }]; });
    }
    setLoading(false);
  };

  const remaining = DAILY_LIMIT - questionsToday;

  return (
    <div style={{fontFamily:"Inter, sans-serif",display:"flex",flexDirection:"column",height:"calc(100vh - 200px)",minHeight:"500px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"20px"}}>
        <div>
          <h2 style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"2.5rem",letterSpacing:"2px",background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",margin:0}}>AI COACH CHAT</h2>
          <p style={{color:"#555",margin:"5px 0 0 0",fontSize:"0.85rem"}}>Ask your AI coach anything about boxing</p>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{background: remaining > 0 ? "rgba(233,69,96,0.1)" : "rgba(255,255,255,0.05)",border: remaining > 0 ? "1px solid rgba(233,69,96,0.3)" : "1px solid rgba(255,255,255,0.08)",borderRadius:"10px",padding:"8px 16px"}}>
            <p style={{color: remaining > 0 ? "#e94560" : "#444",margin:0,fontWeight:"700",fontSize:"1rem"}}>{remaining}/{DAILY_LIMIT}</p>
            <p style={{color:"#444",margin:0,fontSize:"0.75rem"}}>questions left today</p>
          </div>
        </div>
      </div>

      <div style={{flex:1,overflowY:"auto",marginBottom:"15px",display:"flex",flexDirection:"column",gap:"12px",paddingRight:"5px"}}>
        {messages.map(function(msg, i) {
          return (
            <div key={i} style={{display:"flex",justifyContent: msg.role === "user" ? "flex-end" : "flex-start"}}>
              {msg.role === "assistant" && (
                <div style={{width:"35px",height:"35px",borderRadius:"50%",background:"linear-gradient(135deg, #e94560, #ff6b35)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1rem",flexShrink:0,marginRight:"10px",alignSelf:"flex-end"}}>🥊</div>
              )}
              <div style={{
                maxWidth:"75%",padding:"14px 18px",
                borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                background: msg.role === "user" ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.05)",
                border: msg.role === "user" ? "none" : "1px solid rgba(255,255,255,0.08)",
                color:"white",fontSize:"0.92rem",lineHeight:"1.6"
              }}>
                {msg.content}
              </div>
            </div>
          );
        })}
        {loading && (
          <div style={{display:"flex",justifyContent:"flex-start"}}>
            <div style={{width:"35px",height:"35px",borderRadius:"50%",background:"linear-gradient(135deg, #e94560, #ff6b35)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1rem",marginRight:"10px"}}>🥊</div>
            <div style={{padding:"14px 18px",borderRadius:"18px 18px 18px 4px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)"}}>
              <div style={{display:"flex",gap:"4px",alignItems:"center"}}>
                <div style={{width:"6px",height:"6px",borderRadius:"50%",background:"#e94560"}}></div>
                <div style={{width:"6px",height:"6px",borderRadius:"50%",background:"#e94560"}}></div>
                <div style={{width:"6px",height:"6px",borderRadius:"50%",background:"#e94560"}}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {remaining === 0 ? (
        <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"14px",padding:"20px",textAlign:"center"}}>
          <p style={{color:"#555",margin:"0 0 5px 0",fontSize:"0.9rem"}}>Daily limit reached 🥊</p>
          <p style={{color:"#333",margin:0,fontSize:"0.8rem"}}>Come back tomorrow for 5 more questions. Upgrade for unlimited chat.</p>
        </div>
      ) : (
        <div style={{display:"flex",gap:"10px",alignItems:"flex-end"}}>
          <textarea
            value={input}
            onChange={function(e) { setInput(e.target.value); }}
            onKeyDown={function(e) { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }}}
            placeholder="Ask your coach anything... (Enter to send)"
            rows={2}
            style={{flex:1,padding:"14px",backgroundColor:"rgba(255,255,255,0.04)",color:"white",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"14px",fontSize:"0.95rem",fontFamily:"Inter, sans-serif",resize:"none",outline:"none",lineHeight:"1.5"}}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            style={{padding:"14px 20px",background: !input.trim() || loading ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg, #e94560, #ff6b35)",color: !input.trim() || loading ? "#444" : "white",border:"none",borderRadius:"14px",cursor: !input.trim() || loading ? "not-allowed" : "pointer",fontFamily:"Inter, sans-serif",fontWeight:"700",fontSize:"0.9rem",whiteSpace:"nowrap"}}>
            Send 🥊
          </button>
        </div>
      )}
    </div>
  );
}

export default ChatCoach;
